import React, { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, FieldArray } from "formik";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PlanUsagePreview from "./PlanUsagePreview";
import * as Yup from "yup";

import {
  fetchSubscriptionPlanById,
  addSubscriptionPlan,
  updateSubscriptionPlan,
  fetchAllSubscriptionPlanModules,
  fetchCountry,
  fetchSubscriptionPlanFeatures,
  fetchSubscriptionPlanTags
} from "../../../slices/thunks";

const SubscriptionPlanSchema = Yup.object().shape({
  PlanCode: Yup.string()
    .required("Plan code is required")
    .matches(/^[A-Z_]+$/, "Use uppercase letters only"),

  NameEn: Yup.string()
    .required("Plan name is required")
    .min(3, "Minimum 3 characters"),

  NameAr: Yup.string()
    .required("Plan name is required")
    .min(3, "Minimum 3 characters"),

  DescriptionEn: Yup.string()
    .required("Plan description is required")
    .min(3, "Minimum 3 characters"),

  DescriptionAr: Yup.string()
    .required("Plan description is required")
    .min(3, "Minimum 3 characters"),

  Price: Yup.number()
    .required("Price is required")
    .min(0, "Price cannot be negative"),

  TagID: Yup.number().optional().nullable(true),

  CountryID: Yup.number()
    .required("Country is required"),

  BillingCycle: Yup.string()
    .oneOf(["MONTHLY", "YEARLY"])
    .required("Billing cycle is required"),

  Modules: Yup.array()
    .of(
      Yup.object().shape({
        ModuleID: Yup.number().required("Module is required"),
        AccessLevel: Yup.string()
          .oneOf(["NONE", "FULL", "READ", "LIMITED"])
          .required("Access level is required"),
      })
    )
    .min(1, "At least one module is required"),

  Limits: Yup.array().of(
    Yup.object().shape({
      FeatureID: Yup.string().required("Feature is required"),
      LimitValue: Yup.number()
        .required("Limit value is required")
        .min(1, "Limit must be greater than 0"),
      LimitPeriod: Yup.string()
        .oneOf(["MB", "DAY", "MONTH", "PAGE", "WORDS", "MIN", "DURATION", "UNLIMITED", "TOTAL"])
        .required("Limit period is required"),
    })
  ),
});

const emptyPlan = {
  ID: null,
  NameEn: "",
  NameAr: "",
  DescriptionEn: "",
  DescriptionAr: "",
  PlanCode: "",
  CountryID: 113,
  Price: 0,
  BillingCycle: "MONTHLY",
  Modules: [],
  Limits: [],
};

function SubscriptionPlanEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { loading, selectedPlan, modules, features, tags, country } = useSelector((state) => ({
    loading: state.SubscriptionPlan.loading,
    selectedPlan: state.SubscriptionPlan.selectedPlan,
    features: state.SubscriptionPlan.features,
    tags: state.SubscriptionPlan.tags,
    modules: state.SubscriptionPlanModule.modules,
    country: state.Module.country,
  }));

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    if (isEdit) {
      dispatch(fetchSubscriptionPlanById({ ID: id }));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    dispatch(fetchCountry());
    dispatch(fetchSubscriptionPlanTags());
    dispatch(fetchAllSubscriptionPlanModules());
    dispatch(fetchSubscriptionPlanFeatures());

  }, [])

  const initialValues = isEdit && selectedPlan ? selectedPlan : emptyPlan;

  /* ---------------- UI ---------------- */
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? t("EditSubscriptionPlan") : t("AddSubscriptionPlan")}
        />

        {loading ? (
          <div className="text-center">
            <Spinner />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={SubscriptionPlanSchema}
            onSubmit={async(values, { setSubmitting }) => {
              try {
                setSubmitting(true);
                if (isEdit) {
                  await dispatch(updateSubscriptionPlan(values)).unwrap();
                } else {
                  await dispatch(addSubscriptionPlan(values)).unwrap();
                }
                navigate("/payment/subscription-plans");

              } catch (error) {
                console.log('error: ', error);

              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              isSubmitting
            }) => {
              
              useEffect(() => {
                if (
                  !isEdit &&
                  modules.length > 0 &&
                  values.Modules.length === 0
                ) {
                  const initialModules = modules.map(mod => ({
                    ModuleID: mod.ID,
                    AccessLevel: "NONE"
                  }));

                  setFieldValue("Modules", initialModules);
                }
              }, [modules, isEdit, values.Modules.length, setFieldValue]);

              useEffect(() => {
                if (
                  !isEdit &&
                  features.length > 0 &&
                  values.Limits.length === 0
                ) {
                  const initialLimits = features.map(feature => ({
                    FeatureID: feature.ID,
                    LimitValue: 0,
                    LimitPeriod: "DAY"
                  }));

                  setFieldValue("Limits", initialLimits);
                }
              }, [features, isEdit, values.Limits.length, setFieldValue]);

              return <Form>
                <Row>
                  {/* ================= LEFT FORM ================= */}
                  <Col md={8}>
                    {/* BASIC INFO */}
                    <Card className="mb-3">
                      <CardHeader>{t('Basic Information')}</CardHeader>
                      <CardBody>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Input
                              name="NameEn"
                              placeholder={t('Plan Name (English)')}
                              value={values.NameEn}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.NameEn && !!errors.NameEn}
                            />
                            <div className="invalid-feedback">
                              {t(errors.NameEn)}
                            </div>
                          </Col>

                          <Col md={6}>
                            <Input
                              name="NameAr"
                              placeholder={t('Plan Name (Arabic)')}
                              value={values.NameAr}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.NameAr && !!errors.NameAr}
                            />
                            <div className="invalid-feedback">
                              {t(errors.NameAr)}
                            </div>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                              <Input
                                type="textarea"
                                row={2}
                                name="DescriptionEn"
                                placeholder={t('Plan Description (English)')}
                                value={values.DescriptionEn}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={touched.DescriptionEn && !!errors.DescriptionEn}
                              />
                              <div className="invalid-feedback">
                                {t(errors.DescriptionEn)}
                              </div>
                            </Col>

                            <Col md={6}>
                              <Input
                                type="textarea"
                                row={2}
                                name="DescriptionAr"
                                placeholder={t('Plan Description (Arabic)')}
                                value={values.DescriptionAr}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={touched.DescriptionAr && !!errors.DescriptionAr}
                              />
                              <div className="invalid-feedback">
                                {t(errors.DescriptionAr)}
                              </div>
                            </Col>
                        </Row>
                        <Row>
                          <Col md={3}>
                            <Input
                              name="PlanCode"
                              placeholder={t('Plan Code')}
                              value={values.PlanCode}
                              disabled={isEdit}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.PlanCode && !!errors.PlanCode}
                            />
                            <div className="invalid-feedback">
                              {t(errors.PlanCode)}
                            </div>
                          </Col>

                          <Col md={3}>
                            <Input
                                type="select"
                                name="TagID"
                                value={values.TagID}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                // invalid={touched.TagID && !!errors.TagID}
                              >
                                <option value="">{t('Select Tag')}</option>
                                {tags.map((tag, i) => (
                                  <option
                                    key={i}
                                    value={tag.ID}
                                  >
                                    {i18n.language == 'en' ? tag.NameEn : tag.NameAr}
                                  </option>
                                ))}
                            </Input>
                            {/* <div className="invalid-feedback">
                              {errors.TagID}
                            </div> */}
                          </Col>

                          <Col md={2}>
                            <Input
                                type="select"
                                name="CountryID"
                                value={values.CountryID}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={touched.CountryID && !!errors.CountryID}
                              >
                                <option value="">{t('Select Currency')}</option>
                                {country.map((c, i) => (
                                  <option
                                    key={i}
                                    value={c.ID}
                                  >
                                    {`${c[i18n.language]} (${c.CurrencyCode})`}
                                  </option>
                                ))}
                            </Input>
                            <div className="invalid-feedback">
                              {t(errors.CountryID)}
                            </div>
                          </Col>

                          <Col md={2}>
                            <Input
                              type="number"
                              name="Price"
                              placeholder={t('Price')}
                              value={values.Price}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              dir={i18n.language === "ar" ? "rtl" : "ltr"}
                              invalid={touched.Price && !!errors.Price}
                            />
                            <div className="invalid-feedback">
                              {t(errors.Price)}
                            </div>
                          </Col>

                          <Col md={2}>
                            <Input
                              type="select"
                              name="BillingCycle"
                              value={values.BillingCycle}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={touched.BillingCycle && !!errors.BillingCycle}
                            >
                              <option value="MONTHLY">{t('MONTHLY')}</option>
                              <option value="YEARLY">{t('YEARLY')}</option>
                            </Input>
                            <div className="invalid-feedback">
                              {t(errors.BillingCycle)}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* MODULES */}
                    {/* <Card className="mb-3">
                      <CardHeader>
                        Modules
                        <Button
                          size="sm"
                          color="primary"
                          className="float-end"
                          onClick={() =>
                            setFieldValue("Modules", [
                              ...values.Modules,
                              { ModuleID: "", AccessLevel: "NONE" },
                            ])
                          }
                        >
                          + Add Module
                        </Button>
                      </CardHeader>
                      <CardBody>
                        <FieldArray name="Modules">
                          {({ remove }) =>
                            values.Modules.map((m, i) => (
                              <Row key={i} className="mb-2">
                                <Col md={6}>
                                  <Input
                                    type="select"
                                    name={`Modules.${i}.ModuleID`}
                                    value={m.ModuleID}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Modules?.[i]?.ModuleID &&
                                      errors.Modules?.[i]?.ModuleID
                                    }
                                  >
                                    <option value="">Select Module</option>
                                    {modules.map((mod, i) => (
                                      <option
                                        key={i}
                                        value={mod.ID}
                                      >
                                        {i18n.language == 'en' ? mod.NameEn : mod.NameAr}
                                      </option>
                                    ))}
                                  </Input>
                                  <div className="invalid-feedback">
                                    {errors.Modules?.[i]?.ModuleID}
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <Input
                                    type="select"
                                    name={`Modules.${i}.AccessLevel`}
                                    value={m.AccessLevel}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Modules?.[i]?.AccessLevel &&
                                      errors.Modules?.[i]?.AccessLevel
                                    }
                                  >
                                    <option value="NONE">None</option>
                                    <option value="FULL">Full</option>
                                    <option value="READ">Read</option>
                                    <option value="LIMITED">Limited</option>
                                  </Input>
                                  <div className="invalid-feedback">
                                    {errors.Modules?.[i]?.AccessLevel}
                                  </div>
                                </Col>

                                <Col md={2}>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => remove(i)}
                                  >
                                    ✕
                                  </Button>
                                </Col>
                              </Row>
                            ))
                          }
                        </FieldArray>
                      </CardBody>
                    </Card> */}

                     <Card className="mb-3">
                      <CardHeader>
                        {t("Modules")}
                      </CardHeader>
                      <CardBody>
                        <FieldArray name="Modules">
                          {({ remove }) =>
                            values.Modules.map((m, i) => (
                              <Row key={i} className="mb-2">
                                <Col md={6}>
                                  <Input
                                    type="select"
                                    name={`Modules.${i}.ModuleID`}
                                    value={m.ModuleID}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled
                                    invalid={
                                      touched.Modules?.[i]?.ModuleID &&
                                      errors.Modules?.[i]?.ModuleID
                                    }
                                  >
                                    <option value="">{t('Select Module')}</option>
                                    {modules.map((mod, i) => (
                                      <option
                                        key={i}
                                        value={mod.ID}
                                      >
                                        {i18n.language == 'en' ? mod.NameEn : mod.NameAr}
                                      </option>
                                    ))}
                                  </Input>
                                  <div className="invalid-feedback">
                                    {t(errors.Modules?.[i]?.ModuleID)}
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <Input
                                    type="select"
                                    name={`Modules.${i}.AccessLevel`}
                                    value={m.AccessLevel}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Modules?.[i]?.AccessLevel &&
                                      errors.Modules?.[i]?.AccessLevel
                                    }
                                  >
                                    <option value="NONE">{t('NONE')}</option>
                                    <option value="FULL">{t('FULL')}</option>
                                    <option value="READ">{t('READ')}</option>
                                    <option value="LIMITED">{t('LIMITED')}</option>
                                  </Input>
                                  <div className="invalid-feedback">
                                    {t(errors.Modules?.[i]?.AccessLevel)}
                                  </div>
                                </Col>
                              </Row>
                            ))
                          }
                        </FieldArray>
                      </CardBody>
                    </Card>

                    {/* LIMITS */}
                    <Card className="mb-3">
                      <CardHeader>
                        {t('Usage Limits')}
                        {/* <Button
                          size="sm"
                          color="primary"
                          className="ms-2 float-end"
                          onClick={() =>
                            setFieldValue("Limits", [
                              ...values.Limits,
                              {
                                FeatureID: "",
                                LimitValue: 0,
                                LimitPeriod: "DAY",
                              },
                            ])
                          }
                        >
                          + {t('Add Limit')}
                        </Button> */}
                      </CardHeader>
                      {/* <CardBody>
                        <FieldArray name="Limits">
                          {({ remove }) =>
                            values.Limits.map((l, i) => (
                              <Row key={i} className="mb-2">
                                <Col md={4}>
                                  <Input
                                    type="select"
                                    name={`Limits.${i}.FeatureID`}
                                    value={l.FeatureID}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Limits?.[i]?.FeatureID &&
                                      errors.Limits?.[i]?.FeatureID
                                    }
                                  >
                                    <option value="">{t('Select Feature')}</option>
                                    {features.map((feature, i) => (
                                      <option
                                        key={i}
                                        value={feature.ID}
                                      >
                                        {feature.Feature}
                                      </option>
                                    ))}
                                  </Input>
                                  <div className="invalid-feedback">
                                    {t(errors.Limits?.[i]?.FeatureID)}
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <Input
                                    type="number"
                                    name={`Limits.${i}.LimitValue`}
                                    placeholder="Limit"
                                    value={l.LimitValue}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Limits?.[i]?.LimitValue &&
                                      errors.Limits?.[i]?.LimitValue
                                    }
                                  />
                                  <div className="invalid-feedback">
                                    {t(errors.Limits?.[i]?.LimitValue)}
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <Input
                                    type="select"
                                    name={`Limits.${i}.LimitPeriod`}
                                    value={l.LimitPeriod}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.Limits?.[i]?.LimitPeriod &&
                                      errors.Limits?.[i]?.LimitPeriod
                                    }
                                  >
                                    <option value="">{t('select')}</option>
                                    <option value="MB">{t('MB')}</option>
                                    <option value="DAY">{t('Per Day')}</option>
                                    <option value="MONTH">{t('Per Month')}</option>
                                    <option value="TOTAL">{t('Total')}</option>
                                  </Input>
                                  <div className="invalid-feedback">
                                    {t(errors.Limits?.[i]?.LimitPeriod)}
                                  </div>
                                </Col>
                                <Col md={2}>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => remove(i)}
                                  >
                                    ✕
                                  </Button>
                                </Col>
                              </Row>
                            ))
                          }
                        </FieldArray>
                      </CardBody> */}
                      <CardBody>
                        <FieldArray name="Limits">
                          {({ remove }) => {

                            // 1️⃣ Collect selected FeatureIDs
                            const selectedFeatureIds = values.Limits
                            .map(l => l.FeatureID.toString())
                            .filter(Boolean);

                            return (
                              <>
                                {values.Limits.map((l, i) => (
                                  <Row key={i} className="mb-2">
                                    
                                    {/* ---------- Feature Select ---------- */}
                                    <Col md={4}>
                                      <Input
                                        type="select"
                                        name={`Limits.${i}.FeatureID`}
                                        value={l.FeatureID}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled
                                        invalid={
                                          touched.Limits?.[i]?.FeatureID &&
                                          errors.Limits?.[i]?.FeatureID
                                        }
                                      >
                                        <option value="">{t('Select Feature')}</option>

                                        {features
                                          .filter(feature =>
                                            // Allow current row selection
                                            feature.ID == l.FeatureID ||
                                            !selectedFeatureIds.includes(feature.ID.toString())
                                          )
                                          .map(feature => (
                                            <option key={feature.ID} value={feature.ID}>
                                              {feature.Feature}
                                            </option>
                                          ))}
                                      </Input>

                                      <div className="invalid-feedback">
                                        {t(errors.Limits?.[i]?.FeatureID)}
                                      </div>
                                    </Col>

                                    {/* ---------- Limit Value ---------- */}
                                    <Col md={3}>
                                      <Input
                                        type="number"
                                        name={`Limits.${i}.LimitValue`}
                                        placeholder={t('Limit')}
                                        value={l.LimitValue}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        // dir={i18n.language === "ar" ? "rtl" : "ltr"}
                                        invalid={
                                          touched.Limits?.[i]?.LimitValue &&
                                          errors.Limits?.[i]?.LimitValue
                                        }
                                      />
                                      <div className="invalid-feedback">
                                        {t(errors.Limits?.[i]?.LimitValue)}
                                      </div>
                                    </Col>

                                    {/* ---------- Limit Period ---------- */}
                                    <Col md={3}>
                                      <Input
                                        type="select"
                                        name={`Limits.${i}.LimitPeriod`}
                                        value={l.LimitPeriod}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        invalid={
                                          touched.Limits?.[i]?.LimitPeriod &&
                                          errors.Limits?.[i]?.LimitPeriod
                                        }
                                      >
                                        <option value="">{t('Select')}</option>
                                        <option value="MB">{t('MB')}</option>
                                        <option value="DAY">{t('Per Day')}</option>
                                        <option value="MONTH">{t('Per Month')}</option>
                                        <option value="PAGE">{t('Page')}</option>
                                        <option value="WORDS">{t('Words')}</option>
                                        <option value="MIN">{t('Min')}</option>
                                        <option value="DURATION">{t('Duration')}</option>
                                        <option value="UNLIMITED">{t('Unlimited')}</option>
                                        <option value="TOTAL">{t('Total')}</option>
                                      </Input>

                                      <div className="invalid-feedback">
                                        {t(errors.Limits?.[i]?.LimitPeriod)}
                                      </div>
                                    </Col>

                                    {/* ---------- Remove Button ---------- */}
                                    {/* <Col md={2} className="text-center">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => remove(i)}
                                      >
                                        ✕
                                      </Button>
                                    </Col> */}
                                  </Row>
                                ))}
                              </>
                            );
                          }}
                        </FieldArray>
                      </CardBody>
                    </Card>

                    {/* ACTIONS */}
                    <div className="d-flex text-end gap-2 mb-3">
                      <Button
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button color="success" type="submit" disabled={isSubmitting}>
                        {t('Save')}
                      </Button>
                    </div>
                  </Col>

                  {/* ================= PREVIEW ================= */}
                  <Col md={4}>
                    <PlanUsagePreview plan={values} />
                  </Col>
                </Row>
              </Form>
            }}
          </Formik>
        )}
      </Container>
    </div>
  );
}

export default SubscriptionPlanEditor;